import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import CardTile from "@/components/CardTile";
import CaptureDialog from "@/components/CaptureDialog";
import { ArrowLeft, Plus, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export default function SpaceDetail() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [space, setSpace] = useState(null);
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [otherCards, setOtherCards] = useState([]);
  const [selected, setSelected] = useState({});
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/cards", { params: { space_id: spaceId } }),
      api.get("/spaces"),
    ]);
    setCards(c.data);
    setSpace(s.data.find(x => x.space_id === spaceId));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [spaceId]);

  const openAdd = async () => {
    const all = (await api.get("/cards")).data;
    setOtherCards(all.filter(c => !(c.space_ids || []).includes(spaceId)));
    setSelected({}); setQ("");
    setAddOpen(true);
  };
  const commitAdd = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) { setAddOpen(false); return; }
    await api.post(`/spaces/${spaceId}/cards`, { card_ids: ids });
    toast.success(`Added ${ids.length} card${ids.length > 1 ? "s" : ""}`);
    setAddOpen(false);
    load();
  };
  const removeFromSpace = async (card) => {
    await api.delete(`/spaces/${spaceId}/cards/${card.card_id}`);
    load();
  };
  const onEdit = (c) => { setEditing(c); setOpen(true); };
  const onDelete = async (c) => {
    if (!window.confirm("Delete this card?")) return;
    await api.delete(`/cards/${c.card_id}`);
    load();
  };
  const toggleCheck = async (card, item) => {
    const next = card.checklist.map(x => x.id === item.id ? { ...x, done: !x.done } : x);
    await api.patch(`/cards/${card.card_id}`, { checklist: next });
    load();
  };

  const filtered = otherCards.filter(c => {
    if (!q) return true;
    const s = (c.title || "") + " " + (c.content || "") + " " + (c.url || "") + " " + (c.tags || []).join(" ");
    return s.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="relative min-h-screen px-10 pt-10 pb-16">
      <div className="orb orb-purple" style={{ width: 500, height: 500, top: -180, left: 200, opacity: 0.35 }} />
      <div className="orb orb-orange" style={{ width: 400, height: 400, bottom: -180, right: 100, opacity: 0.15 }} />

      <button onClick={() => navigate("/spaces")} className="text-white/50 hover:text-white flex items-center gap-1 text-sm mb-6"><ArrowLeft size={14} /> All spaces</button>
      <div className="flex items-center justify-between mb-4 relative flex-wrap gap-4">
        <h1 className="serif-italic text-5xl text-white/85 flex items-center gap-3">
          <span className="w-3 h-3 rounded-full border" style={{ borderColor: space?.color }} />
          {space?.name || "Space"}
        </h1>
        <div className="flex gap-2">
          <button onClick={openAdd} className="pill" data-testid="space-add-existing"><Layers size={13} /> Add existing</button>
          <button onClick={() => { setEditing(null); setOpen(true); }} className="pill" data-testid="space-add-card" style={{ background: "var(--orange)", borderColor: "var(--orange)" }}><Plus size={13} /> New card</button>
        </div>
      </div>
      <div className="h-px bg-white/10 mb-10" />
      {cards.length === 0 ? (
        <div className="text-center mt-24">
          <div className="serif-italic text-3xl text-white/60">This space is empty.</div>
          <p className="text-white/40 mt-3 text-sm">Use "Add existing" to gather cards into this space, or capture a new one.</p>
        </div>
      ) : (
        <div className="masonry relative">
          {cards.map(c => <CardTile key={c.card_id} card={c} onToggleCheck={toggleCheck} onEdit={onEdit} onDelete={onDelete} onRemoveFromSpace={removeFromSpace} />)}
        </div>
      )}
      <CaptureDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); load(); } }} editing={editing} initialSpaceId={editing ? null : spaceId} onCreated={load} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl bg-[color:var(--surface)] border-white/10 max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">Add existing cards to “{space?.name}”</DialogTitle></DialogHeader>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-white/5 border-white/10" data-testid="space-add-search" />
          <div className="overflow-y-auto flex-1 space-y-1 -mx-1 px-1">
            {filtered.length === 0 && <div className="text-white/40 text-sm py-8 text-center">No other cards to add.</div>}
            {filtered.map(c => (
              <label key={c.card_id} className="flex items-start gap-3 p-3 rounded-md hover:bg-white/[0.03] cursor-pointer" data-testid={`space-add-option-${c.card_id}`}>
                <Checkbox checked={!!selected[c.card_id]} onCheckedChange={(v) => setSelected(s => ({ ...s, [c.card_id]: !!v }))} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white/90 truncate">{c.title || c.content?.slice(0, 60) || c.url || "(untitled)"}</div>
                  <div className="text-xs text-white/40 mt-0.5 capitalize">{c.type}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end"><Button onClick={commitAdd} style={{ background: "var(--orange)" }} data-testid="space-add-commit">Add selected</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
